import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { 
  LayoutDashboard, 
  CheckSquare, 
  ShoppingBasket, 
  Wrench, 
  Settings, 
  Menu,
  LogOut,
  Calendar,
  Moon,
  Sun,
  Building2,
  ChevronDown,
  User as UserIcon,
  Bell,
  Home,
  Info,
  X,
  Users,
  User as UserCircle,
  Layers,
  Shield
} from 'lucide-react';

// 🛰️ Unified types and Firebase hooks
import { UserProfile, SafetyMode, Household, AppAction, LayoutNotification, DataScope } from '@/types';
import { db } from '@/firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';

interface LayoutProps {
  children: React.ReactNode;
  user: UserProfile;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  currentHousehold?: Household;
  notifications: LayoutNotification[];
  dispatch: React.Dispatch<AppAction>;
  onLogout: () => void;
}

/**
 * 🏛️ MASTER LAYOUT ENGINE
 * Orchestrates navigation, hybrid scoping, and global system notifications.
 */
export const Layout: React.FC<LayoutProps> = ({ 
  children, 
  user, 
  theme, 
  onToggleTheme,
  currentHousehold,
  notifications = [],
  dispatch,
  onLogout
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // 🛡️ Safety Gate: Strict check for Child/Restricted mode
  const isChildMode = user.safetyMode === SafetyMode.STRICT;

  // 📂 NAVIGATION REGISTRY
  const allNavItems = useMemo(() => [ // Use useMemo to avoid re-creating on every render
    { icon: LayoutDashboard, label: t('nav.dashboard', 'Dashboard'), path: '/dashboard' },
    { icon: Calendar, label: t('nav.calendar', 'Calendar'), path: '/calendar' },
    { icon: CheckSquare, label: t('nav.tasks', 'Tasks'), path: '/tasks' },
    { icon: Users, label: t('nav.contacts', 'Contacts'), path: '/contacts' },
    { icon: ShoppingBasket, label: t('nav.inventory', 'Inventory'), path: '/groceries' },
    { icon: Wrench, label: t('nav.maintenance', 'Maintenance'), path: '/maintenance' },
    { icon: Settings, label: t('nav.settings', 'Household'), path: '/settings' },
  ], [t]); 

  // 🛡️ Partitioning navigation based on safety mode
  const navItems = useMemo(() => {
    let filteredItems = allNavItems;

    // Filter for Child Mode
    if (isChildMode) {
      filteredItems = filteredItems.filter(item => ['/dashboard', '/tasks', '/calendar'].includes(item.path));
    }

    // Filter Maintenance if no currentHousehold
    if (!currentHousehold) {
      filteredItems = filteredItems.filter(item => item.path !== '/maintenance');
      filteredItems = filteredItems.filter(item => item.path !== '/contacts');
    }

    return filteredItems;
  }, [allNavItems, isChildMode, currentHousehold]);

  const isActive = (path: string) => {
    return location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));
  };

  const getPageTitle = () => {
    const current = allNavItems.find(item => item.path === location.pathname);
    if (location.pathname === '/bug-report') return t('nav.reportBug', 'Report Bug');
    if (location.pathname === '/user-settings') return t('nav.userSettings', 'User Settings');
    if (location.pathname === '/contacts') return t('contacts.title', 'Directory');
    if (location.pathname === '/changelog') return "Protocol History"; 
    return current ? current.label : t('nav.dashboard', 'Dashboard');
  };

  // 🕵️ Outside Click Listeners for Dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔔 NOTIFICATION LOGIC
  const unreadNotifications = notifications.filter(n => !n.readBy?.includes(user.id));
  const unreadCount = unreadNotifications.length;

  const markRead = async (id: string) => {
    if (!currentHousehold) return;
    const nRef = doc(db, "households", currentHousehold.id, "notifications", id);
    try {
      await updateDoc(nRef, { readBy: arrayUnion(user.id) });
    } catch (e) { console.error("Error marking notification read:", e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await Promise.all(unreadNotifications.map(n => markRead(n.id)));
    } catch (e) { console.error("Error marking all read:", e); }
  };

  const isDashboardPage = useMemo(() => location.pathname === '/dashboard', [location.pathname]);

  // 🔄 HYBRID SCOPE TOGGLE LOGIC
  // Bridges Personal Partition (/users/uid) and Household Hub (/households/hid)
  const currentScope = user.defaultScope || 'BOTH'; // Use user.defaultScope directly from UserProfile
  const handleScopeChange = (scope: DataScope) => {
    if (!currentHousehold && scope !== 'PERSONAL') {
        alert(t('scope.no_household_alert', 'You must be part of a household to use Shared or Hybrid modes.'));
        return;
    }
    dispatch({ type: 'SET_DATA_SCOPE', payload: scope });
    // Also persist this change to user's profile in Firestore
    if (user.id) {
        updateDoc(doc(db, "users", user.id), { defaultScope: scope }).catch(e => console.error("Failed to save defaultScope:", e));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col md:flex-row transition-colors duration-300">
      
      {/* 📱 Mobile Top Bar */}
      <div className="md:hidden bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-4 flex items-center justify-between sticky top-0 z-[60]">
        <div className="flex items-center gap-2 font-black text-xl tracking-tighter text-indigo-600">
          <Home className="w-6 h-6" />
          <span>Everyday</span>
        </div>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* 🏛️ Sidebar Navigation */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:h-screen flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        `}
      >
        <div className="p-8 flex items-center gap-3 font-black text-2xl tracking-tighter">
          <div className="p-1.5 rounded-xl text-white shadow-lg bg-indigo-600">
            <Home className="w-6 h-6" />
          </div>
          <span className="text-slate-950 dark:text-white">Everyday<span className="text-indigo-600">OS</span></span>
        </div>

        <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto no-scrollbar">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-200 ${
                  active 
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' 
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-indigo-600'
                }`}
              >
                <item.icon className={`w-5 h-5 ${active ? 'text-white' : 'text-slate-400'}`} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* 🏢 Household Context Indicator */}
        <div className="p-4 mt-auto">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-[1.5rem] p-4 flex items-center gap-3 border border-slate-200 dark:border-slate-700">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentHousehold ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30'}`}>
              {currentHousehold ? <Building2 className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{t('nav.household', 'Protocol Context')}</p>
              <p className="text-sm font-bold text-slate-950 dark:text-white truncate">{currentHousehold?.name || 'Personal Mode'}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* 🏁 Main Content Wrapper */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
        <header className="h-20 flex items-center justify-between px-8 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50 z-40">
          <h1 className="text-2xl font-black text-slate-950 dark:text-white tracking-tight italic uppercase">{getPageTitle()}</h1>
          
          <div className="flex items-center gap-6">
            
            {/* 🛡️ HYBRID SCOPE TOGGLE (The Privacy Pivot) */}
            {currentHousehold && (
              <div className="hidden lg:flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200 dark:border-slate-700">
                <button 
                  onClick={() => handleScopeChange('PERSONAL')}
                  className={clsx(
                    "flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    currentScope === 'PERSONAL' ? "bg-white dark:bg-slate-900 text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  <UserCircle className="w-3 h-3" /> {t('common.personal', 'Personal')}
                </button>
                <button 
                  onClick={() => handleScopeChange('BOTH')}
                  className={clsx(
                    "flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    currentScope === 'BOTH' ? "bg-white dark:bg-slate-900 text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  <Layers className="w-3 h-3" /> {t('common.hybrid', 'Hybrid')}
                </button>
                <button 
                  onClick={() => handleScopeChange('SHARED')}
                  className={clsx(
                    "flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    currentScope === 'SHARED' ? "bg-white dark:bg-slate-900 text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  <Users className="w-3 h-3" /> {t('common.shared', 'Shared')}
                </button>
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* 🔔 Notifications Drawer */}
              <div className="relative" ref={notificationsRef}>
                <button 
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className={`p-2.5 rounded-xl border transition-all relative ${
                    isNotificationsOpen ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 text-indigo-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500'
                  }`}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white border-2 border-white dark:border-slate-900">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {isNotificationsOpen && (
                  <div className="absolute right-0 mt-3 w-80 bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                      <h3 className="font-black text-[10px] uppercase text-slate-400 tracking-widest">Alert Ledger</h3>
                      {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-[10px] font-black text-indigo-600 hover:underline">Clear All</button>}
                    </div>
                    <div className="max-h-80 overflow-y-auto p-2 space-y-1 no-scrollbar">
                      {notifications.length === 0 ? (
                        <div className="py-12 text-center text-slate-400 text-xs font-bold uppercase tracking-widest italic opacity-50">Zero Signal Found</div>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} className={`p-4 rounded-2xl transition-colors ${n.readBy?.includes(user.id) ? 'opacity-50' : 'bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <Info className="w-3 h-3 text-indigo-500" />
                              <p className="text-xs font-black text-slate-800 dark:text-slate-200">{n.title}</p>
                            </div>
                            <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 leading-relaxed">{n.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 👤 Profile Identity Menu */}
              <div className="relative" ref={profileMenuRef}>
                <button 
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center gap-3 p-1 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all"
                >
                  <img 
                    src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}`} 
                    className="w-9 h-9 rounded-xl object-cover" 
                    alt="profile" 
                  />
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform hidden md:block ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-slate-900 rounded-[1.5rem] shadow-2xl border border-slate-200 dark:border-slate-700 py-2 z-50 animate-in fade-in zoom-in-95">
                    <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 mb-2">
                      <p className="text-xs font-black text-slate-900 dark:text-white truncate">{user.name}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{user.role}</p>
                    </div>
                    <button onClick={() => { onToggleTheme(); setIsProfileMenuOpen(false); }} className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800">
                      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />} {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </button>
                    <button onClick={() => { navigate('/user-settings'); setIsProfileMenuOpen(false); }} className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800">
                      <UserCircle className="w-4 h-4" /> Account Settings
                    </button>
                    <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                    <button onClick={onLogout} className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20">
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main
          className={clsx(
            "flex-1 px-0 bg-slate-50 dark:bg-slate-950 overflow-y-auto no-scrollbar" // 🌟 ADDED pt-20 and overflow-y-auto, no-scrollbar here
          )}
        >
          <div className={clsx( // 🌟 Adjusted padding for the inner content div
            "pb-20",
            isDashboardPage ? 'pt-0' : 'md:py-8', // py-4 removed as pt-20 on main covers it. md:py-8 is still ok for desktop
            !isDashboardPage && 'px-8' // Apply horizontal padding only if not dashboard
          )}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};